import { LinearStepper } from '@diligentcorp/atlas-theme-mui-lens';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useMutation } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom-v5-compat';
import { DELETE_DRAFT, ReportStatus, UpdateReport } from 'src/api';
import axios from 'src/axios';
import {
  Button,
  CardBody,
  CardContainer,
  Center,
  Divider,
  Spinner,
} from 'src/components/atoms';
import { ShadowsSize } from 'src/components/foundations/tokens';
import { FlexViewPrimitive } from 'src/components/layout/FlexView/FlexView.styles';
import type { DynamicQuestionAnswerValue } from 'src/components/molecules';
import { handleErrorToast, handleSuccessToast } from 'src/components/molecules';
import { CancelModal } from 'src/components/molecules/CancelModal';
import {
  AppointCaseManagerModal,
  type AppointCaseManagerModalResult,
} from 'src/components/organisms/AppointCaseManagerModal';
import { appointCaseManagerModalMessages } from 'src/components/organisms/AppointCaseManagerModal/appointCaseManagerModalMessages';
import { DraftCaseAuditLog } from 'src/components/organisms/DraftCaseAuditLog';
import { DraftCaseDates } from 'src/components/organisms/DraftCaseDates/DraftCaseDates';
import { DraftCaseDetails } from 'src/components/organisms/DraftCaseDetails/DraftCaseDetails';
import { DraftCaseHeader } from 'src/components/organisms/DraftCaseHeader/DraftCaseHeader';
import { DraftCaseReporter } from 'src/components/organisms/DraftCaseReporter/DraftCaseReporter';
import {
  ensureDynamicQuestionsResponse,
  fetchDraftReport,
  saveDraftReport,
  updateLocalDraft,
} from 'src/creators/MCC.creators';
import {
  SubmitDraftReportMutationVariables,
  useDisclosureCaseByIdQuery,
  useSubmitDraftReportMutation,
} from 'src/graphql/generated';
import { useIsFeatureEnabled, useStepper } from 'src/hooks';
import { usePreventNavigation } from 'src/hooks/navigation/usePreventNavigation';
import { AppDispatch } from 'src/store';
import * as Types from 'src/types';
import { FeatureFlag } from 'src/types';
import { parseUploadedFilesFromAnswerValue } from 'src/utils/dynamicQuestionUploadedFiles';
import { toSubmitDraftReportCaseLeadId } from 'src/utils/toSubmitDraftReportCaseLeadId';
import {
  ManualIntakeDynamicQuestions,
  type ManualIntakeDynamicQuestionsRef,
  type SubmitReportPayload,
} from '../ManualIntakeDynamicQuestions';
import { DraftContainer, StepContent, StickyFooter } from './CreateCase.styles';

const STEP_LABELS = [
  'Reporting details',
  'Select form',
  'Questions',
  'Assign case manager',
];

export const CreateCase = () => {
  const dispatch = useDispatch<AppDispatch>();
  const history = useHistory();
  const navigate = useNavigate();
  const location = useLocation();
  const [, route, reportID] = location.pathname.split('/');
  const isExistingReport = route === 'create-existing-report';
  const currentReport = useSelector(
    (state: Types.State) =>
      state?.manualCaseCreation?.mccReducer?.currentReport ?? null,
  );
  const loading = useSelector(
    (state: Types.State) =>
      state?.manualCaseCreation?.mccReducer?.loading ?? false,
  );
  const [noReporterAvailable, setNoReporterAvailable] = useState(false);

  const isLegacyMode = useSelector(
    (state: Types.State) => state.organisation?.isLegacyMode,
  );

  const isUpdateRelationshipToOrganisationEnabled = useIsFeatureEnabled(
    FeatureFlag.RELATIONSHIP_TO_ORG,
  );

  const isSpeakUpCasesModernizationEnabled = useIsFeatureEnabled(
    FeatureFlag.SPEAK_UP_CASES_MODERNIZATION,
  );

  const {
    currentStep,
    steps: stepperSteps,
    goToNextStep,
    goToPreviousStep,
    isFirstStep,
    isLastStep,
  } = useStepper(STEP_LABELS);

  useEffect(() => {
    if (!reportID || reportID === 'new') return;
    const fetchDraft = async () => await dispatch(fetchDraftReport(reportID));
    fetchDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportID]);

  useEffect(() => {
    if (
      !currentReport ||
      (!currentReport.User &&
        !currentReport.user &&
        !currentReport.userUpdates?.email)
    ) {
      setNoReporterAvailable(true);
    } else {
      setNoReporterAvailable(false);
    }
  }, [currentReport]);

  const isSavedToDb = useSelector(
    (state: Types.State) =>
      state?.manualCaseCreation?.mccReducer?.isSavedToDb ?? false,
  );
  const saving = useSelector(
    (state: Types.State) =>
      state?.manualCaseCreation?.mccReducer?.saving ?? false,
  );
  const storedReportID = useSelector(
    (state: Types.State) =>
      state?.manualCaseCreation?.mccReducer?.currentReport?.report ?? null,
  );
  const lastSavedResponseId = useSelector(
    (state: Types.State) =>
      state?.manualCaseCreation?.mccReducer?.lastSavedResponseId ?? null,
  );

  const hasReporterSet = Boolean(
    currentReport?.user ?? currentReport?.userUpdates?.email,
  );

  const shouldWarnBeforeLeaving =
    !isLegacyMode && !loading && currentReport != null;

  const {
    showCancellationModal,
    preventNavigation,
    setNextLocation,
    onStay,
    onLeave,
    setShowCancellationModal,
    setPreventNavigation,
  } = usePreventNavigation(shouldWarnBeforeLeaving, '/cases');

  const [auditLogOpen, setAuditLogOpen] = useState(false);
  const auditLogReportID =
    reportID === 'new' ? (storedReportID ?? 'new') : reportID;

  const caseId = currentReport?.cases?.[0]?.id ?? null;
  const caseFromReport = currentReport?.cases?.[0] as
    | {
        id?: string;
        DisclosuresCaseData?: {
          questionnaireId?: string;
          responseId?: string;
        };
      }
    | undefined;
  const { data: disclosureData } = useDisclosureCaseByIdQuery(
    { caseId: caseId ?? '' },
    {
      enabled:
        !isLegacyMode &&
        Boolean(caseId) &&
        currentReport?.status === ReportStatus.DRAFT,
    },
  );

  const questionnaireResponseIdFromCase = useMemo(() => {
    const disclosure = disclosureData?.disclosureCaseById as
      | { id?: string }
      | null
      | undefined;
    return (
      disclosure?.id ?? caseFromReport?.DisclosuresCaseData?.responseId ?? null
    );
  }, [disclosureData?.disclosureCaseById, caseFromReport]);

  const dynamicQuestionsInitialState = useMemo(() => {
    const disclosure = disclosureData?.disclosureCaseById as
      | {
          id?: string;
          questionnaireId?: string;
          answers?: Array<{
            questionId?: string;
            questionText?: string;
            value?: unknown;
          }>;
        }
      | null
      | undefined;
    const questionnaireId =
      disclosure?.questionnaireId ??
      caseFromReport?.DisclosuresCaseData?.questionnaireId;
    const responseId =
      disclosure?.id ?? caseFromReport?.DisclosuresCaseData?.responseId;
    if (!questionnaireId || !responseId) return null;
    const initialAnswers: Record<string, DynamicQuestionAnswerValue> = {};
    disclosure?.answers?.forEach((answer) => {
      if (answer?.value === undefined) return;
      const answerValue = answer.value as DynamicQuestionAnswerValue;
      const uploadedFiles = parseUploadedFilesFromAnswerValue(answer.value);
      const normalized = {
        text: answerValue?.text,
        selectedOptionId: answerValue?.selectedOptionId,
        selectedOptionIds: answerValue?.selectedOptionIds,
        dataGridRows: answerValue?.dataGridRows,
        ...(uploadedFiles.length > 0 && {
          uploadedFiles,
          fileNames: uploadedFiles.map((f) => f.fileName),
        }),
      };
      const questionId =
        answer?.questionId != null ? String(answer.questionId) : null;
      if (questionId != null) initialAnswers[questionId] = normalized;
      const questionText = answer?.questionText?.trim();
      if (questionText != null)
        initialAnswers[`questionText:${questionText}`] = normalized;
    });
    return {
      initialQuestionnaireId: questionnaireId,
      initialResponseId: responseId,
      initialAnswers,
    };
  }, [disclosureData?.disclosureCaseById, caseFromReport]);

  const dynamicQuestionsRef = useRef<ManualIntakeDynamicQuestionsRef>(null);
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);
  const pendingReviewSubmitPayloadRef = useRef<SubmitReportPayload | null>(
    null,
  );
  const pendingCaseManagerSubmitPayloadRef = useRef<SubmitReportPayload | null>(
    null,
  );
  const [showAppointCaseManager, setShowAppointCaseManager] = useState(false);

  const currentUser = useSelector(
    (state: Types.State) => state?.globalUsers?.currentUser as Types.User,
  );
  const { mutate: submitDraftReportMutation } = useSubmitDraftReportMutation({
    onSuccess: () => {
      setIsSubmittingReport(false);
      setNextLocation({ pathname: '/cases' });
      setPreventNavigation(false);
      handleSuccessToast({
        toastMessage: 'Case submitted successfully',
        toastDescription: 'You successfully submitted a case report',
      });
    },
    onError: () => {
      setIsSubmittingReport(false);
      handleErrorToast({
        toastMessage: 'Failed to submit draft report',
      });
    },
  });

  const updateLocalReportDetails = async (report: UpdateReport) => {
    if (!isLegacyMode) {
      dispatch(updateLocalDraft(report));
    }
  };

  const handleSaveDraft = useCallback(() => {
    if (!currentReport) return;

    const payload = dynamicQuestionsRef.current?.getDynamicQuestionsPayload();
    const existingReportID = isSavedToDb ? storedReportID : 'new';
    const routePrefix = isExistingReport
      ? '/create-existing-report'
      : '/create-report';

    dispatch(
      saveDraftReport(
        currentReport,
        existingReportID,
        isExistingReport,
        ({ reportId }) => {
          const newPathname = `${routePrefix}/${reportId}`;
          setNextLocation({ pathname: newPathname });
          if (location.pathname !== newPathname) {
            // flushSync forces the Prompt to re-render with when=false before
            // history.replace fires, preventing the CancelModal from appearing.
            flushSync(() => setPreventNavigation(false));
            history.replace(`${newPathname}${location.search}`);
            setPreventNavigation(true);
          }
        },
        payload ?? undefined,
      ),
    );
  }, [
    currentReport,
    isSavedToDb,
    storedReportID,
    isExistingReport,
    location.pathname,
    location.search,
    history,
    setNextLocation,
    dispatch,
    setPreventNavigation,
  ]);

  const { mutate: deleteDraftReport } = useMutation({
    mutationFn: () => axios.delete(DELETE_DRAFT(reportID)),
    onSuccess: () => {
      setNextLocation({ pathname: '/cases' });
      setPreventNavigation(false);
      handleSuccessToast({ toastMessage: 'Draft report successfully deleted' });
    },
    onError: () => {
      handleErrorToast({ toastMessage: 'Failed to delete draft report' });
    },
  });

  const handleDeleteDraft = () => {
    if (reportID && reportID !== 'new') {
      deleteDraftReport();
    } else {
      setNextLocation({ pathname: '/cases' });
      setPreventNavigation(false);
    }
  };

  const executeSubmitReport = useCallback(
    async (
      payload: SubmitReportPayload,
      cm: {
        assignAutomatically: boolean;
        selectedCaseLeadId?: number | string;
      },
    ) => {
      setIsSubmittingReport(true);
      try {
        if (!hasReporterSet) {
          setIsSubmittingReport(false);
          handleErrorToast({
            toastMessage: 'Please set reporter details before submitting',
          });
          return;
        }
        let reportId = isSavedToDb ? storedReportID : reportID;
        let responseId = payload.responseId ?? null;

        if (!reportId || reportId === 'new') {
          const draftPayload = {
            questionnaireId: payload.questionnaireId,
            responseId: payload.responseId ?? undefined,
            answers: payload.answers,
          };
          const saveResult = (await dispatch(
            saveDraftReport(
              currentReport,
              reportID,
              isExistingReport,
              undefined,
              draftPayload,
              { skipSuccessToast: true },
            ),
          )) as { reportId?: string; responseId?: string } | undefined;
          reportId = saveResult?.reportId ?? null;
          responseId = responseId ?? saveResult?.responseId ?? null;
          if (!reportId) {
            setIsSubmittingReport(false);
            handleErrorToast({
              toastMessage: 'Unable to save draft before submit',
            });
            return;
          }
        }

        if (!responseId) {
          responseId = await dispatch(
            ensureDynamicQuestionsResponse(currentReport, reportId, {
              questionnaireId: payload.questionnaireId,
              responseId,
              answers: payload.answers,
            }),
          );
        }
        if (!responseId) {
          setIsSubmittingReport(false);
          handleErrorToast({
            toastMessage: 'Unable to prepare form response for submit',
          });
          return;
        }

        const variables: SubmitDraftReportMutationVariables = {
          reportId,
          submissionMode: Types.SUBMISSION_MODE.MCC,
          responseId,
          attestationAccepted: payload.attestationAccepted,
        };

        if (cm.assignAutomatically) {
          variables.autoAssignCaseManagerViaRouting = true;
        } else {
          const caseLead = toSubmitDraftReportCaseLeadId(cm.selectedCaseLeadId);
          if (caseLead !== undefined) {
            variables.caseLead = caseLead;
            variables.autoAssignCaseManagerViaRouting = false;
          }
        }

        submitDraftReportMutation(variables);
      } catch (err) {
        setIsSubmittingReport(false);
        handleErrorToast({
          toastMessage:
            err instanceof Error
              ? `Unable to submit report: ${err.message}`
              : 'Unable to submit report due to an unexpected error.',
        });
      }
    },
    [
      hasReporterSet,
      isSavedToDb,
      storedReportID,
      reportID,
      isExistingReport,
      currentReport,
      dispatch,
      submitDraftReportMutation,
    ],
  );

  const handleProceedToCaseManagerModal = (payload: SubmitReportPayload) => {
    pendingReviewSubmitPayloadRef.current = payload;
    const snapshot = payload;
    if (!snapshot) {
      handleErrorToast({
        toastMessage:
          'Submit data was lost. Please return to review and submit the case again.',
      });
      return;
    }
    pendingCaseManagerSubmitPayloadRef.current = snapshot;
    setShowAppointCaseManager(true);
  };

  const handleAppointCaseManagerConfirm = useCallback(
    (result: AppointCaseManagerModalResult) => {
      const payload = pendingCaseManagerSubmitPayloadRef.current;
      if (!payload) {
        handleErrorToast({
          toastMessage:
            'Submit data was lost. Please close this dialog and submit the case again.',
        });
        return;
      }
      if (!result.assignAutomatically && result.selectedUser == null) {
        handleErrorToast({
          toastMessage: appointCaseManagerModalMessages.selectCaseManagerToast,
        });
        return;
      }
      pendingCaseManagerSubmitPayloadRef.current = null;
      pendingReviewSubmitPayloadRef.current = null;
      setShowAppointCaseManager(false);
      void executeSubmitReport(payload, {
        assignAutomatically: result.assignAutomatically,
        selectedCaseLeadId: result.selectedUser?.id,
      });
    },
    [executeSubmitReport],
  );

  const handleAppointCaseManagerClose = useCallback(() => {
    setShowAppointCaseManager(false);
    pendingCaseManagerSubmitPayloadRef.current = null;
  }, []);

  if (isLegacyMode) {
    return null;
  }

  if (loading) {
    return (
      <DraftContainer $modernized={isSpeakUpCasesModernizationEnabled}>
        <Center>
          <Spinner variant="secondary" size="large" />
        </Center>
      </DraftContainer>
    );
  }

  return (
    <DraftContainer $modernized={isSpeakUpCasesModernizationEnabled}>
      <CancelModal
        size="medium"
        title="Leave case creation?"
        description="If you leave now, any unsaved progress on this report will be lost."
        showCancellationModal={showCancellationModal}
        preventNavigation={preventNavigation && shouldWarnBeforeLeaving}
        setNextLocation={setNextLocation}
        onConfirm={onStay}
        onCancel={onLeave}
        setOpen={setShowCancellationModal}
        setShowCancellationModal={setShowCancellationModal}
        data-testid="create-case-cancel-modal"
      />
      <DraftCaseHeader
        isExistingReport={isExistingReport}
        onBack={() => navigate('/cases')}
        onSaveDraft={handleSaveDraft}
        saveButtonDisabled={saving || noReporterAvailable}
        saveButtonLoading={saving}
        onAuditLog={() => setAuditLogOpen(true)}
        onDelete={handleDeleteDraft}
        isModernized={isSpeakUpCasesModernizationEnabled}
      />
      <DraftCaseAuditLog
        isOpen={auditLogOpen}
        setIsOpen={setAuditLogOpen}
        reportID={auditLogReportID}
      />
      {isSpeakUpCasesModernizationEnabled ? (
        <>
          <LinearStepper
            steps={stepperSteps}
            activeStepIndex={currentStep}
            hasButton={false}
          />
          <StepContent
            data-testid={`create-case-step-content-${currentStep}`}
          />
          <StickyFooter>
            <Button
              variant="tertiary"
              data-testid="create-case-previous-button"
              onClick={goToPreviousStep}
              disabled={isFirstStep}
            >
              Previous
            </Button>
            <Button
              variant="primary"
              data-testid="create-case-next-button"
              onClick={goToNextStep}
            >
              {isLastStep ? 'Submit case' : 'Next'}
            </Button>
          </StickyFooter>
        </>
      ) : (
        <>
          <CardContainer $shadowSize={ShadowsSize.large}>
            <CardBody>
              <FlexViewPrimitive $gap={2} $direction="row">
                <DraftCaseReporter
                  currentReport={currentReport}
                  updateReport={updateLocalReportDetails}
                  isUpdateRelationshipToOrganisationEnabled={
                    isUpdateRelationshipToOrganisationEnabled
                  }
                />
                <Divider variant="vertical" height="auto" />
                <DraftCaseDetails
                  currentReport={currentReport}
                  updateReport={updateLocalReportDetails}
                  isExistingReport={isExistingReport}
                />
                {isExistingReport && (
                  <>
                    <Divider variant="vertical" height="auto" />
                    <DraftCaseDates
                      currentReport={currentReport}
                      updateReport={updateLocalReportDetails}
                    />
                  </>
                )}
              </FlexViewPrimitive>
            </CardBody>
          </CardContainer>
          <CardContainer $shadowSize={ShadowsSize.large}>
            <CardBody>
              <ManualIntakeDynamicQuestions
                ref={dynamicQuestionsRef}
                {...dynamicQuestionsInitialState}
                savedResponseId={
                  lastSavedResponseId ?? questionnaireResponseIdFromCase ?? null
                }
                evidenceUploadReportId={
                  storedReportID ?? (reportID !== 'new' ? reportID : null)
                }
                canSelectQuestionnaire={hasReporterSet && isSavedToDb}
                onSubmitReportFromReview={handleProceedToCaseManagerModal}
                canSubmitReport={hasReporterSet}
                isSubmittingReport={isSubmittingReport}
                currentReport={currentReport}
                updateReport={updateLocalReportDetails}
              />
              <AppointCaseManagerModal
                open={showAppointCaseManager}
                setOpen={(nextOpen) => {
                  if (!nextOpen) {
                    handleAppointCaseManagerClose();
                  }
                }}
                currentReport={currentReport}
                currentUser={currentUser}
                onConfirm={handleAppointCaseManagerConfirm}
                isSubmitting={isSubmittingReport}
                data-testid="create-case-appoint-cm"
              />
            </CardBody>
          </CardContainer>
        </>
      )}
    </DraftContainer>
  );
};
